import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Grid, 
  Header, 
  Segment, 
  Tab, 
  Table, 
  Statistic, 
  Loader, 
  Message, 
  Button, 
  Form, 
  Dropdown,
  Label,
  Progress,
  Icon,
  Container,
  Divider,
  Popup
} from 'semantic-ui-react';
import 'react-datepicker/dist/react-datepicker.css';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer, 
  BarChart, 
  Bar, 
  PieChart, 
  Pie, 
  Cell,
  AreaChart,
  Area,
  RadialBarChart,
  RadialBar
} from 'recharts';
import { API, showError, showSuccess, isAdmin } from '../../helpers';

const DataAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [timeRange, setTimeRange] = useState('7d'); // 7d, 30d, 90d
  const [refreshing, setRefreshing] = useState(false);
  
  // 系统概览数据（新API）
  const [overview, setOverview] = useState(null);
  
  // 趋势数据
  const [trends, setTrends] = useState([]);
  
  // 留存分析数据
  const [retention, setRetention] = useState(null);
  
  // 用户分群数据
  const [segments, setSegments] = useState([]);
  
  // 错误分析数据
  const [errorAnalysis, setErrorAnalysis] = useState(null);
  
  // 模型使用分析
  const [modelUsage, setModelUsage] = useState(null);
  
  // 渠道分析
  const [channelAnalysis, setChannelAnalysis] = useState([]);
  
  // API统计数据
  const [apiStats, setApiStats] = useState(null);
  
  // 存储分析数据
  const [storageOverview, setStorageOverview] = useState(null);
  const [tableStats, setTableStats] = useState([]);
  
  // 个人统计数据（保留原有逻辑）
  const [personalStats, setPersonalStats] = useState(null);
  const [personalTrend, setPersonalTrend] = useState([]);
  const [personalHourly, setPersonalHourly] = useState([]);
  const [personalModels, setPersonalModels] = useState([]);

  const panes = [
    {
      menuItem: { key: 'overview', icon: 'dashboard', content: '系统概览' },
      render: () => (
        <Tab.Pane>
          <SystemOverviewTab 
            overview={overview}
            trends={trends}
            timeRange={timeRange}
            onTimeRangeChange={setTimeRange}
          />
        </Tab.Pane>
      )
    },
    {
      menuItem: { key: 'personal', icon: 'user', content: '个人统计' },
      render: () => (
        <Tab.Pane>
          <PersonalStatsTab 
            stats={personalStats}
            trend={personalTrend}
            hourly={personalHourly}
            models={personalModels}
            timeRange={timeRange}
            onTimeRangeChange={setTimeRange}
          />
        </Tab.Pane>
      )
    }
  ];

  // 如果是管理员，添加管理员专用标签页
  if (isAdmin()) {
    panes.push({
      menuItem: { key: 'retention', icon: 'heart', content: '留存分析' },
      render: () => (
        <Tab.Pane>
          <RetentionAnalysisTab 
            retention={retention}
            timeRange={timeRange}
            onTimeRangeChange={setTimeRange}
          />
        </Tab.Pane>
      )
    });
    
    panes.push({
      menuItem: { key: 'segments', icon: 'users', content: '用户分群' },
      render: () => (
        <Tab.Pane>
          <UserSegmentsTab 
            segments={segments}
            overview={overview}
            timeRange={timeRange}
            onTimeRangeChange={setTimeRange}
          />
        </Tab.Pane>
      )
    });
    
    panes.push({
      menuItem: { key: 'quality', icon: 'shield', content: '质量分析' },
      render: () => (
        <Tab.Pane>
          <QualityAnalysisTab 
            errorAnalysis={errorAnalysis}
            modelUsage={modelUsage}
            channelAnalysis={channelAnalysis}
            timeRange={timeRange}
            onTimeRangeChange={setTimeRange}
          />
        </Tab.Pane>
      )
    });
    
    panes.push({
      menuItem: { key: 'api', icon: 'code', content: 'API分析' },
      render: () => (
        <Tab.Pane>
          <APIAnalysisTab 
            apiStats={apiStats}
            timeRange={timeRange}
            onTimeRangeChange={setTimeRange}
          />
        </Tab.Pane>
      )
    });
    
    panes.push({
      menuItem: { key: 'storage', icon: 'database', content: '存储分析' },
      render: () => (
        <Tab.Pane>
          <StorageAnalysisTab 
            overview={storageOverview}
            tableStats={tableStats}
          />
        </Tab.Pane>
      )
    });
  }

  useEffect(() => {
    loadAllData();
  }, [timeRange]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadOverview(),
        loadTrends(),
        loadPersonalStats(),
        isAdmin() ? loadAdminData() : Promise.resolve(),
        isAdmin() ? loadStorageData() : Promise.resolve()
      ]);
    } catch (error) {
      console.error('加载数据失败:', error);
      showError('加载数据失败: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadOverview = async () => {
    try {
      const res = await API.get('/api/admin/analytics/overview');
      if (res.data.success) {
        setOverview(res.data.data);
      }
    } catch (error) {
      console.error('加载概览数据失败:', error);
    }
  };

  const loadTrends = async () => {
    try {
      const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
      const res = await API.get(`/api/admin/analytics/trends?days=${days}`);
      if (res.data.success) {
        setTrends(res.data.data);
      }
    } catch (error) {
      console.error('加载趋势数据失败:', error);
    }
  };

  const loadPersonalStats = async () => {
    try {
      // 加载个人汇总
      const summaryRes = await API.get('/api/user/behavior/summary');
      if (summaryRes.data.success) {
        setPersonalStats(summaryRes.data.data);
      }
      
      // 加载个人趋势
      const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
      const trendRes = await API.get(`/api/user/behavior/trend?days=${days}`);
      if (trendRes.data.success) {
        setPersonalTrend(trendRes.data.data);
      }
      
      // 加载小时分布
      const hourlyRes = await API.get('/api/user/behavior/hourly');
      if (hourlyRes.data.success) {
        const data = Object.entries(hourlyRes.data.data || {}).map(([hour, count]) => ({
          hour: `${hour}:00`,
          requests: count
        }));
        setPersonalHourly(data);
      }
      
      // 加载模型使用
      const modelRes = await API.get('/api/user/behavior/models');
      if (modelRes.data.success) {
        const data = Object.entries(modelRes.data.data || {}).map(([model, count]) => ({
          name: model,
          value: count,
          percentage: 0
        }));
        setPersonalModels(data);
      }
    } catch (error) {
      console.error('加载个人数据失败:', error);
    }
  };

  const loadStorageData = async () => {
    try {
      const overviewRes = await API.get('/api/admin/storage/overview');
      if (overviewRes.data.success) {
        setStorageOverview(overviewRes.data.data);
      }
      
      const tablesRes = await API.get('/api/admin/storage/mysql/tables');
      if (tablesRes.data.success) {
        setTableStats(tablesRes.data.data || []);
      }
    } catch (error) {
      console.error('加载存储数据失败:', error);
    }
  };

  const loadAdminData = async () => {
    try {
      // 加载留存分析
      const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
      const retentionRes = await API.get(`/api/admin/analytics/retention?days=${days}`);
      if (retentionRes.data.success) {
        setRetention(retentionRes.data.data);
      }
      
      // 加载用户分群
      const segmentsRes = await API.get('/api/admin/analytics/segments');
      if (segmentsRes.data.success) {
        setSegments(segmentsRes.data.data);
      }
      
      // 加载错误分析
      const errorDays = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
      const errorRes = await API.get(`/api/admin/analytics/errors?days=${errorDays}`);
      if (errorRes.data.success) {
        setErrorAnalysis(errorRes.data.data);
      }
      
      // 加载模型使用分析
      const modelRes = await API.get(`/api/admin/analytics/models?days=${errorDays}`);
      if (modelRes.data.success) {
        setModelUsage(modelRes.data.data);
      }
      
      // 加载渠道分析
      const channelRes = await API.get(`/api/admin/analytics/channels?days=${errorDays}`);
      if (channelRes.data.success) {
        setChannelAnalysis(channelRes.data.data);
      }
      
      // 加载API统计
      const apiStatsRes = await API.get(`/api/admin/analytics/api-stats?days=${errorDays}`);
      if (apiStatsRes.data.success) {
        setApiStats(apiStatsRes.data.data);
      }
    } catch (error) {
      console.error('加载管理员数据失败:', error);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadAllData();
    setRefreshing(false);
    showSuccess('数据刷新完成');
  };

  const handleCollect = async () => {
    try {
      const res = await API.post('/api/admin/analytics/collect');
      if (res.data.success) {
        showSuccess('统计任务已触发');
      } else {
        showError(res.data.message || '触发失败');
      }
    } catch (error) {
      showError('触发统计任务失败: ' + error.message);
    }
  };

  if (loading) {
    return (
      <Container style={{ padding: '40px 0' }}>
        <Segment placeholder>
          <Header icon>
            <Icon name='chart line' />
            正在加载数据统计...
          </Header>
          <Loader active inline='centered' size='large' />
        </Segment>
      </Container>
    );
  }

  return (
    <Container fluid style={{ padding: '20px' }}>
      <Segment basic>
        <Grid stackable>
          <Grid.Row>
            <Grid.Column width={10}>
              <Header as='h1' icon>
                <Icon name='chart area' />
                <Header.Content>
                  数据统计分析
                  <Header.Subheader>
                    系统使用情况与用户行为分析
                  </Header.Subheader>
                </Header.Content>
              </Header>
            </Grid.Column>
            <Grid.Column width={6} textAlign='right'>
              <Button.Group>
                <Button 
                  icon='clock'
                  content={timeRange === '7d' ? '7天' : timeRange === '30d' ? '30天' : '90天'}
                  onClick={() => {
                    const ranges = ['7d', '30d', '90d'];
                    const currentIndex = ranges.indexOf(timeRange);
                    const nextIndex = (currentIndex + 1) % ranges.length;
                    setTimeRange(ranges[nextIndex]);
                  }}
                />
                <Button 
                  icon='refresh'
                  content='刷新'
                  loading={refreshing}
                  onClick={handleRefresh}
                  primary
                />
                {isAdmin() && (
                  <Popup
                    content='手动触发每日统计数据收集'
                    trigger={
                      <Button 
                        icon='database'
                        content='收集'
                        onClick={handleCollect}
                        color='teal'
                      />
                    }
                  />
                )}
              </Button.Group>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Segment>

      <Tab 
        panes={panes} 
        activeIndex={activeTab}
        onTabChange={(e, { activeIndex }) => setActiveTab(activeIndex)}
        menu={{ 
          secondary: true, 
          pointing: true,
          color: 'blue'
        }}
      />
    </Container>
  );
};

// 系统概览标签页
const SystemOverviewTab = ({ overview, trends, timeRange, onTimeRangeChange }) => {
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82ca9d'];

  if (!overview) {
    return (
      <Segment placeholder>
        <Header icon>
          <Icon name='search' />
          暂无系统统计数据
        </Header>
        <Button primary onClick={() => window.location.reload()}>
          刷新页面
        </Button>
      </Segment>
    );
  }

  // 格式化变化率显示
  const formatChange = (value) => {
    if (value > 0) return `+${value.toFixed(1)}%`;
    if (value < 0) return `${value.toFixed(1)}%`;
    return '0%';
  };

  const getChangeColor = (value) => {
    if (value > 0) return 'green';
    if (value < 0) return 'red';
    return 'grey';
  };

  return (
    <div>
      {/* 核心指标卡片 */}
      <Grid stackable>
        <Grid.Row>
          <Grid.Column width={4}>
            <Card fluid color='blue'>
              <Card.Content>
                <Statistic color='blue'>
                  <Statistic.Value>
                    <Icon name='user' />
                    {overview.dau || 0}
                  </Statistic.Value>
                  <Statistic.Label>日活跃用户 (DAU)</Statistic.Label>
                </Statistic>
                <Divider />
                <div style={{ textAlign: 'center' }}>
                  <Label color={getChangeColor(overview.dau_change)} size='small'>
                    {formatChange(overview.dau_change)} 较昨日
                  </Label>
                </div>
              </Card.Content>
            </Card>
          </Grid.Column>
          
          <Grid.Column width={4}>
            <Card fluid color='green'>
              <Card.Content>
                <Statistic color='green'>
                  <Statistic.Value>
                    <Icon name='users' />
                    {overview.wau || 0}
                  </Statistic.Value>
                  <Statistic.Label>周活跃用户 (WAU)</Statistic.Label>
                </Statistic>
                <Divider />
                <div style={{ textAlign: 'center' }}>
                  <Label color={getChangeColor(overview.wau_change)} size='small'>
                    {formatChange(overview.wau_change)} 较上周
                  </Label>
                </div>
              </Card.Content>
            </Card>
          </Grid.Column>
          
          <Grid.Column width={4}>
            <Card fluid color='orange'>
              <Card.Content>
                <Statistic color='orange'>
                  <Statistic.Value>
                    <Icon name='globe' />
                    {overview.mau || 0}
                  </Statistic.Value>
                  <Statistic.Label>月活跃用户 (MAU)</Statistic.Label>
                </Statistic>
                <Divider />
                <div style={{ textAlign: 'center' }}>
                  <Label color={getChangeColor(overview.mau_change)} size='small'>
                    {formatChange(overview.mau_change)} 较上月
                  </Label>
                </div>
              </Card.Content>
            </Card>
          </Grid.Column>
          
          <Grid.Column width={4}>
            <Card fluid color='purple'>
              <Card.Content>
                <Statistic color='purple'>
                  <Statistic.Value>
                    <Icon name='user plus' />
                    {overview.today_new_users || 0}
                  </Statistic.Value>
                  <Statistic.Label>今日新增用户</Statistic.Label>
                </Statistic>
                <Divider />
                <div style={{ textAlign: 'center' }}>
                  <Label color={getChangeColor(overview.today_new_users_change)} size='small'>
                    {formatChange(overview.today_new_users_change)} 较昨日
                  </Label>
                </div>
              </Card.Content>
            </Card>
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column width={4}>
            <Card fluid color='teal'>
              <Card.Content>
                <Statistic color='teal'>
                  <Statistic.Value>
                    <Icon name='chart line' />
                    {(overview.today_requests || 0).toLocaleString()}
                  </Statistic.Value>
                  <Statistic.Label>今日请求数</Statistic.Label>
                </Statistic>
                <Divider />
                <Progress 
                  percent={overview.success_rate || 0} 
                  color='green' 
                  size='small'
                  label={`成功率 ${(overview.success_rate || 0).toFixed(1)}%`}
                />
              </Card.Content>
            </Card>
          </Grid.Column>
          
          <Grid.Column width={4}>
            <Card fluid color='yellow'>
              <Card.Content>
                <Statistic color='yellow'>
                  <Statistic.Value>
                    <Icon name='clock' />
                    {overview.avg_latency || 0}ms
                  </Statistic.Value>
                  <Statistic.Label>平均响应时间</Statistic.Label>
                </Statistic>
                <Divider />
                <div style={{ textAlign: 'center' }}>
                  <Label color={overview.avg_latency < 1000 ? 'green' : overview.avg_latency < 3000 ? 'yellow' : 'red'} size='small'>
                    {overview.avg_latency < 1000 ? '优秀' : overview.avg_latency < 3000 ? '良好' : '需优化'}
                  </Label>
                </div>
              </Card.Content>
            </Card>
          </Grid.Column>
          
          <Grid.Column width={8}>
            <Card fluid color='red'>
              <Card.Content>
                <Header as='h4'>
                  <Icon name='heart' />
                  用户留存率
                </Header>
                <Statistic.Group size='mini' widths='three'>
                  <Statistic color='green'>
                    <Statistic.Value>{(overview.retention?.day1 || 0).toFixed(1)}%</Statistic.Value>
                    <Statistic.Label>次日留存</Statistic.Label>
                  </Statistic>
                  <Statistic color='blue'>
                    <Statistic.Value>{(overview.retention?.day7 || 0).toFixed(1)}%</Statistic.Value>
                    <Statistic.Label>7日留存</Statistic.Label>
                  </Statistic>
                  <Statistic color='purple'>
                    <Statistic.Value>{(overview.retention?.day30 || 0).toFixed(1)}%</Statistic.Value>
                    <Statistic.Label>30日留存</Statistic.Label>
                  </Statistic>
                </Statistic.Group>
              </Card.Content>
            </Card>
          </Grid.Column>
        </Grid.Row>
      </Grid>

      <Divider />

      {/* 趋势图表 */}
      <Grid stackable>
        <Grid.Row>
          <Grid.Column width={8}>
            <Card fluid>
              <Card.Content>
              <Card.Header>
                <Icon name='line graph' />
                  DAU 趋势
              </Card.Header>
              </Card.Content>
              <Card.Content>
                {trends && trends.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={trends}>
                      <defs>
                        <linearGradient id="colorDau" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#8884d8" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="colorNewUsers" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#82ca9d" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#82ca9d" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Area type="monotone" dataKey="dau" stroke="#8884d8" fillOpacity={1} fill="url(#colorDau)" name="日活用户" />
                      <Area type="monotone" dataKey="new_users" stroke="#82ca9d" fillOpacity={1} fill="url(#colorNewUsers)" name="新增用户" />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <Message>暂无趋势数据</Message>
                )}
              </Card.Content>
            </Card>
          </Grid.Column>
          
          <Grid.Column width={8}>
            <Card fluid>
              <Card.Content>
              <Card.Header>
                  <Icon name='chart bar' />
                  请求量趋势
              </Card.Header>
              </Card.Content>
              <Card.Content>
                {trends && trends.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={trends}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="total_requests" fill="#0088FE" name="总请求数" />
                      <Bar dataKey="failed_requests" fill="#FF8042" name="失败请求" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Message>暂无请求数据</Message>
                )}
              </Card.Content>
            </Card>
          </Grid.Column>
        </Grid.Row>
      </Grid>

      <Divider />

      {/* 模型使用 TOP5 */}
      {overview.top_models && overview.top_models.length > 0 && (
        <Card fluid>
          <Card.Content>
            <Card.Header>
              <Icon name='fire' />
              最近7天模型使用 TOP5
            </Card.Header>
          </Card.Content>
          <Card.Content>
            <Grid stackable>
              <Grid.Column width={8}>
                <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                      data={overview.top_models}
                        cx="50%"
                        cy="50%"
                      innerRadius={40}
                      outerRadius={80}
                        paddingAngle={5}
                      dataKey="count"
                      nameKey="model"
                      >
                      {overview.top_models.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
              </Grid.Column>
              <Grid.Column width={8}>
                <Table basic='very' compact>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>模型</Table.HeaderCell>
                      <Table.HeaderCell textAlign='right'>调用次数</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {overview.top_models.map((model, index) => (
                      <Table.Row key={index}>
                        <Table.Cell>
                          <Label circular style={{ backgroundColor: COLORS[index % COLORS.length], color: 'white' }}>
                            {index + 1}
                          </Label>
                          {' '}{model.model}
                        </Table.Cell>
                        <Table.Cell textAlign='right'>
                          <strong>{model.count.toLocaleString()}</strong>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              </Grid.Column>
            </Grid>
          </Card.Content>
        </Card>
      )}
    </div>
  );
};

// 留存分析标签页
const RetentionAnalysisTab = ({ retention, timeRange, onTimeRangeChange }) => {
  if (!retention) {
    return (
      <Segment placeholder>
        <Header icon>
          <Icon name='heart' />
          暂无留存数据
        </Header>
      </Segment>
    );
  }

  const getRetentionColor = (rate) => {
    if (rate >= 50) return 'green';
    if (rate >= 30) return 'yellow';
    if (rate >= 10) return 'orange';
    return 'red';
  };

  return (
    <div>
      {/* 留存率概览 */}
      <Grid stackable>
        <Grid.Row>
          <Grid.Column width={16}>
            <Card fluid color='red'>
              <Card.Content>
                <Card.Header>
                  <Icon name='heart' />
                  平均留存率
                </Card.Header>
              </Card.Content>
              <Card.Content>
                <Statistic.Group size='small' widths='three'>
                  <Statistic color={getRetentionColor(retention.summary?.avg_day1_retention)}>
                    <Statistic.Value>{(retention.summary?.avg_day1_retention || 0).toFixed(1)}%</Statistic.Value>
                    <Statistic.Label>次日留存</Statistic.Label>
                  </Statistic>
                  <Statistic color={getRetentionColor(retention.summary?.avg_day7_retention)}>
                    <Statistic.Value>{(retention.summary?.avg_day7_retention || 0).toFixed(1)}%</Statistic.Value>
                    <Statistic.Label>7日留存</Statistic.Label>
                  </Statistic>
                  <Statistic color={getRetentionColor(retention.summary?.avg_day30_retention)}>
                    <Statistic.Value>{(retention.summary?.avg_day30_retention || 0).toFixed(1)}%</Statistic.Value>
                    <Statistic.Label>30日留存</Statistic.Label>
                  </Statistic>
                </Statistic.Group>
              </Card.Content>
            </Card>
          </Grid.Column>
        </Grid.Row>
      </Grid>

      <Divider />

      {/* Cohort 留存表 */}
        <Card fluid>
        <Card.Content>
          <Card.Header>
            <Icon name='table' />
            Cohort 留存分析表
            <Label size='small' color='blue' style={{ marginLeft: '10px' }}>
              每行代表一个用户群体（按首次活跃日期分组）的留存情况
            </Label>
          </Card.Header>
        </Card.Content>
        <Card.Content style={{ overflowX: 'auto' }}>
          {retention.cohort_table && retention.cohort_table.length > 0 ? (
            <Table celled compact>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>群体日期</Table.HeaderCell>
                  <Table.HeaderCell>新用户数</Table.HeaderCell>
                  <Table.HeaderCell>次日留存</Table.HeaderCell>
                  <Table.HeaderCell>3日留存</Table.HeaderCell>
                  <Table.HeaderCell>7日留存</Table.HeaderCell>
                  <Table.HeaderCell>14日留存</Table.HeaderCell>
                  <Table.HeaderCell>30日留存</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {retention.cohort_table.slice(0, 20).map((row, index) => (
                  <Table.Row key={index}>
                    <Table.Cell>{row.cohort_date}</Table.Cell>
                    <Table.Cell>
                      <Label basic>{row.total_users}</Label>
                    </Table.Cell>
                    <Table.Cell>
                      <Popup
                        content={`${row.day1?.retained || 0}/${row.total_users} 用户留存`}
                        trigger={
                          <Label 
                            color={getRetentionColor(row.day1?.rate)} 
                            size='small'
                            style={{ cursor: 'help' }}
                          >
                            {(row.day1?.rate || 0).toFixed(1)}%
                          </Label>
                        }
                      />
                    </Table.Cell>
                    <Table.Cell>
                      <Popup
                        content={`${row.day3?.retained || 0}/${row.total_users} 用户留存`}
                        trigger={
                          <Label 
                            color={getRetentionColor(row.day3?.rate)} 
                            size='small'
                            style={{ cursor: 'help' }}
                          >
                            {(row.day3?.rate || 0).toFixed(1)}%
                          </Label>
                        }
                      />
                    </Table.Cell>
                    <Table.Cell>
                      <Popup
                        content={`${row.day7?.retained || 0}/${row.total_users} 用户留存`}
                        trigger={
                          <Label 
                            color={getRetentionColor(row.day7?.rate)} 
                            size='small'
                            style={{ cursor: 'help' }}
                          >
                            {(row.day7?.rate || 0).toFixed(1)}%
                          </Label>
                        }
                      />
                    </Table.Cell>
                    <Table.Cell>
                      <Popup
                        content={`${row.day14?.retained || 0}/${row.total_users} 用户留存`}
                        trigger={
                          <Label 
                            color={getRetentionColor(row.day14?.rate)} 
                            size='small'
                            style={{ cursor: 'help' }}
                          >
                            {(row.day14?.rate || 0).toFixed(1)}%
                          </Label>
                        }
                      />
                    </Table.Cell>
                    <Table.Cell>
                      <Popup
                        content={`${row.day30?.retained || 0}/${row.total_users} 用户留存`}
                        trigger={
                          <Label 
                            color={getRetentionColor(row.day30?.rate)} 
                            size='small'
                            style={{ cursor: 'help' }}
                          >
                            {(row.day30?.rate || 0).toFixed(1)}%
                          </Label>
                        }
                      />
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          ) : (
            <Message>暂无 Cohort 数据</Message>
          )}
        </Card.Content>
      </Card>
                  </div>
  );
};

// 用户分群标签页
const UserSegmentsTab = ({ segments, overview, timeRange, onTimeRangeChange }) => {
  const COLORS = ['#00C49F', '#0088FE', '#FFBB28', '#FF8042', '#8884D8', '#82ca9d'];

  const segmentColorMap = {
    'new_users': 'green',
    'active_users': 'blue',
    'power_users': 'purple',
    'at_risk': 'orange',
    'dormant': 'yellow',
    'churned': 'red'
  };
                          
                          return (
    <div>
      {/* 用户分群概览 */}
      <Grid stackable>
        <Grid.Row>
          <Grid.Column width={16}>
            <Card fluid>
              <Card.Content>
                <Card.Header>
                  <Icon name='users' />
                  用户分群概览
                </Card.Header>
              </Card.Content>
              <Card.Content>
                {segments && segments.length > 0 ? (
                  <Grid stackable>
                    <Grid.Column width={8}>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={segments}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={100}
                            paddingAngle={3}
                            dataKey="user_count"
                            nameKey="description"
                          >
                            {segments.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </Grid.Column>
                    <Grid.Column width={8}>
                      <Table basic='very'>
                        <Table.Header>
                          <Table.Row>
                            <Table.HeaderCell>分群</Table.HeaderCell>
                            <Table.HeaderCell>用户数</Table.HeaderCell>
                            <Table.HeaderCell>占比</Table.HeaderCell>
                          </Table.Row>
                        </Table.Header>
                        <Table.Body>
                          {segments.map((segment, index) => (
                            <Table.Row key={index}>
                              <Table.Cell>
                                <Label color={segmentColorMap[segment.name] || 'grey'} size='small'>
                                  {segment.description}
                                </Label>
                              </Table.Cell>
                              <Table.Cell>
                                <strong>{segment.user_count}</strong>
                              </Table.Cell>
                              <Table.Cell>
                                <Progress 
                                  percent={segment.percentage} 
                                  size='small'
                                  color={segmentColorMap[segment.name] || 'grey'}
                                  label={`${segment.percentage.toFixed(1)}%`}
                                />
                              </Table.Cell>
                            </Table.Row>
                          ))}
                        </Table.Body>
                      </Table>
                    </Grid.Column>
                  </Grid>
                ) : (
                  <Message>暂无用户分群数据</Message>
            )}
          </Card.Content>
        </Card>
          </Grid.Column>
        </Grid.Row>
      </Grid>

      <Divider />

      {/* 分群说明 */}
      <Card fluid>
        <Card.Content>
          <Card.Header>
            <Icon name='info circle' />
            分群定义说明
          </Card.Header>
        </Card.Content>
        <Card.Content>
          <Grid stackable columns={3}>
            <Grid.Column>
              <Card>
                <Card.Content>
                  <Card.Header>
                    <Label color='green' horizontal>新用户</Label>
                  </Card.Header>
                  <Card.Description>
                    首次活跃在最近7天内的用户，需要重点关注激活和引导
                  </Card.Description>
                </Card.Content>
              </Card>
            </Grid.Column>
            <Grid.Column>
              <Card>
                <Card.Content>
                  <Card.Header>
                    <Label color='blue' horizontal>活跃用户</Label>
                  </Card.Header>
                  <Card.Description>
                    最近7天有活动记录的用户，是产品的核心用户群
                  </Card.Description>
                </Card.Content>
              </Card>
            </Grid.Column>
            <Grid.Column>
              <Card>
                <Card.Content>
                  <Card.Header>
                    <Label color='purple' horizontal>高频用户</Label>
                  </Card.Header>
                  <Card.Description>
                    周请求量≥100的用户，是产品的忠实用户和价值用户
                  </Card.Description>
                </Card.Content>
              </Card>
            </Grid.Column>
            <Grid.Column>
              <Card>
                <Card.Content>
                  <Card.Header>
                    <Label color='orange' horizontal>流失风险</Label>
                  </Card.Header>
                  <Card.Description>
                    7-14天未活动的用户，需要通过召回策略重新激活
                  </Card.Description>
                </Card.Content>
              </Card>
            </Grid.Column>
            <Grid.Column>
              <Card>
                <Card.Content>
                  <Card.Header>
                    <Label color='yellow' horizontal>沉睡用户</Label>
                  </Card.Header>
                  <Card.Description>
                    14-30天未活动的用户，流失风险高，需要特别关注
                  </Card.Description>
                </Card.Content>
              </Card>
            </Grid.Column>
            <Grid.Column>
              <Card>
                <Card.Content>
                  <Card.Header>
                    <Label color='red' horizontal>流失用户</Label>
                  </Card.Header>
                  <Card.Description>
                    30天以上未活动的用户，基本可认定为已流失
                  </Card.Description>
                </Card.Content>
              </Card>
            </Grid.Column>
          </Grid>
        </Card.Content>
      </Card>

      {/* 最近活跃用户 */}
      {overview?.recent_active_users && overview.recent_active_users.length > 0 && (
        <>
          <Divider />
          <Card fluid>
            <Card.Content>
              <Card.Header>
                <Icon name='clock' />
                最近活跃用户
              </Card.Header>
            </Card.Content>
            <Card.Content>
              <Table basic='very' compact>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>用户</Table.HeaderCell>
                    <Table.HeaderCell>最后活跃</Table.HeaderCell>
                    <Table.HeaderCell>7天请求数</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {overview.recent_active_users.map((user, index) => (
                    <Table.Row key={index}>
                      <Table.Cell>
                        <Icon name='user' />
                        {user.username || `用户${user.user_id}`}
                      </Table.Cell>
                      <Table.Cell>
                        {new Date(user.last_active).toLocaleString()}
                      </Table.Cell>
                      <Table.Cell>
                        <Label>{user.requests}</Label>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </Card.Content>
          </Card>
        </>
      )}
    </div>
  );
};

// 个人统计标签页
const PersonalStatsTab = ({ stats, trend, hourly, models, timeRange, onTimeRangeChange }) => {
  if (!stats) {
    return (
      <Segment placeholder>
        <Header icon>
          <Icon name='search' />
          暂无个人统计数据
        </Header>
        <Button primary onClick={() => window.location.reload()}>
          刷新页面
        </Button>
      </Segment>
    );
  }

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  return (
    <div>
      {/* 个人核心指标 */}
      <Grid stackable>
        <Grid.Row>
          <Grid.Column width={3}>
            <Statistic size='small' color='blue'>
              <Statistic.Value>{stats.today_requests || 0}</Statistic.Value>
              <Statistic.Label>今日请求</Statistic.Label>
            </Statistic>
          </Grid.Column>
          <Grid.Column width={3}>
            <Statistic size='small' color='green'>
              <Statistic.Value>{Math.round((stats.today_duration || 0) / 1000)}s</Statistic.Value>
              <Statistic.Label>今日耗时</Statistic.Label>
            </Statistic>
          </Grid.Column>
          <Grid.Column width={3}>
            <Statistic size='small' color='orange'>
              <Statistic.Value>{stats.today_sessions || 0}</Statistic.Value>
              <Statistic.Label>今日会话</Statistic.Label>
            </Statistic>
          </Grid.Column>
          <Grid.Column width={3}>
            <Statistic size='small' color='purple'>
              <Statistic.Value>{stats.peak_hour || 0}:00</Statistic.Value>
              <Statistic.Label>活跃时段</Statistic.Label>
            </Statistic>
          </Grid.Column>
          <Grid.Column width={4}>
            <Card>
              <Card.Content>
                <Card.Header>
                  <Icon name='trophy' />
                  最常用模型
                </Card.Header>
                <Card.Description>
                  <Label size='large' color='blue'>
                    {stats.most_used_model || 'N/A'}
                  </Label>
                </Card.Description>
              </Card.Content>
            </Card>
          </Grid.Column>
        </Grid.Row>
      </Grid>

      <Divider />

      {/* 个人图表 */}
      <Grid stackable>
        <Grid.Row>
          <Grid.Column width={8}>
            <Card fluid>
              <Card.Content>
              <Card.Header>
                <Icon name='line graph' />
                使用趋势
              </Card.Header>
              </Card.Content>
              <Card.Content>
                {trend && trend.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={trend}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Line type="monotone" dataKey="requests" stroke="#8884d8" strokeWidth={2} dot={{ r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <Message>暂无趋势数据</Message>
                )}
              </Card.Content>
            </Card>
          </Grid.Column>
          
          <Grid.Column width={8}>
            <Card fluid>
              <Card.Content>
              <Card.Header>
                <Icon name='clock' />
                24小时活动分布
              </Card.Header>
              </Card.Content>
              <Card.Content>
                {hourly && hourly.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={hourly}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="hour" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="requests" fill="#82ca9d" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Message>暂无小时分布数据</Message>
                )}
              </Card.Content>
            </Card>
          </Grid.Column>
        </Grid.Row>
        
        <Grid.Row>
          <Grid.Column width={16}>
            <Card fluid>
              <Card.Content>
              <Card.Header>
                <Icon name='settings' />
                模型使用偏好
              </Card.Header>
              </Card.Content>
              <Card.Content>
                {models && models.length > 0 ? (
                  <Grid>
                    <Grid.Column width={8}>
                      <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                          <Pie
                            data={models}
                            cx="50%"
                            cy="50%"
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          >
                            {models && models.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </Grid.Column>
                    <Grid.Column width={8}>
                      <Table basic='very'>
                        <Table.Header>
                          <Table.Row>
                            <Table.HeaderCell>模型</Table.HeaderCell>
                            <Table.HeaderCell>使用次数</Table.HeaderCell>
                            <Table.HeaderCell>占比</Table.HeaderCell>
                          </Table.Row>
                        </Table.Header>
                        <Table.Body>
                          {models && models.map((model, index) => {
                            const total = models.reduce((sum, m) => sum + m.value, 0);
                            const percentage = total > 0 ? (model.value / total * 100).toFixed(1) : 0;
                            return (
                              <Table.Row key={index}>
                                <Table.Cell>
                                  <Label circular style={{ backgroundColor: COLORS[index % COLORS.length], color: 'white' }} empty />
                                  {model.name}
                                </Table.Cell>
                                <Table.Cell>{model.value}</Table.Cell>
                                <Table.Cell>{percentage}%</Table.Cell>
                              </Table.Row>
                            );
                          })}
                        </Table.Body>
                      </Table>
                    </Grid.Column>
                  </Grid>
                ) : (
                  <Message>暂无模型使用数据</Message>
                )}
              </Card.Content>
            </Card>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </div>
  );
};

// 质量分析标签页
const QualityAnalysisTab = ({ errorAnalysis, modelUsage, channelAnalysis, timeRange, onTimeRangeChange }) => {
  const COLORS = ['#FF8042', '#FFBB28', '#00C49F', '#0088FE', '#8884D8'];

  return (
    <div>
      {/* 错误概览 */}
      <Grid stackable>
        <Grid.Row>
          <Grid.Column width={16}>
            <Card fluid color='red'>
              <Card.Content>
              <Card.Header>
                  <Icon name='warning sign' />
                  错误概览
              </Card.Header>
              </Card.Content>
              <Card.Content>
                {errorAnalysis ? (
                  <Statistic.Group size='small' widths='four'>
                  <Statistic color='red'>
                      <Statistic.Value>{errorAnalysis.total_errors?.toLocaleString() || 0}</Statistic.Value>
                      <Statistic.Label>总错误数</Statistic.Label>
                  </Statistic>
                  <Statistic color='orange'>
                      <Statistic.Value>{(errorAnalysis.error_rate || 0).toFixed(2)}%</Statistic.Value>
                      <Statistic.Label>错误率</Statistic.Label>
                  </Statistic>
                  <Statistic color='yellow'>
                      <Statistic.Value>{errorAnalysis.main_error_code || '-'}</Statistic.Value>
                      <Statistic.Label>主要错误码</Statistic.Label>
                  </Statistic>
                  <Statistic color='grey'>
                      <Statistic.Value>{errorAnalysis.affected_users || 0}</Statistic.Value>
                      <Statistic.Label>影响用户数</Statistic.Label>
                  </Statistic>
                </Statistic.Group>
                ) : (
                  <Message positive>系统运行正常，暂无错误</Message>
                )}
              </Card.Content>
            </Card>
          </Grid.Column>
        </Grid.Row>
      </Grid>

      <Divider />

      {/* 错误分布 */}
      <Grid stackable>
        <Grid.Row>
          <Grid.Column width={8}>
      <Card fluid>
              <Card.Content>
        <Card.Header>
                  <Icon name='pie chart' />
                  错误类型分布
        </Card.Header>
              </Card.Content>
        <Card.Content>
                {errorAnalysis?.error_distribution && errorAnalysis.error_distribution.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={errorAnalysis.error_distribution}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="count"
                        label={({ message, percent }) => `${message} ${(percent * 100).toFixed(0)}%`}
                      >
                        {errorAnalysis.error_distribution.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <Message positive>暂无错误</Message>
                )}
              </Card.Content>
            </Card>
          </Grid.Column>
          
          <Grid.Column width={8}>
            <Card fluid>
              <Card.Content>
                <Card.Header>
                  <Icon name='list' />
                  错误详情
                </Card.Header>
              </Card.Content>
              <Card.Content>
                {errorAnalysis?.error_distribution && errorAnalysis.error_distribution.length > 0 ? (
                  <Table compact>
                    <Table.Header>
                      <Table.Row>
                        <Table.HeaderCell>状态码</Table.HeaderCell>
                        <Table.HeaderCell>描述</Table.HeaderCell>
                        <Table.HeaderCell>次数</Table.HeaderCell>
                        <Table.HeaderCell>占比</Table.HeaderCell>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {errorAnalysis.error_distribution.slice(0, 10).map((error, index) => (
                        <Table.Row key={index}>
                          <Table.Cell>
                            <Label color='red' size='small'>{error.status}</Label>
                          </Table.Cell>
                          <Table.Cell>{error.message}</Table.Cell>
                          <Table.Cell>{error.count}</Table.Cell>
                          <Table.Cell>{error.percentage?.toFixed(1)}%</Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table>
                ) : (
                  <Message positive>暂无错误</Message>
                )}
              </Card.Content>
            </Card>
          </Grid.Column>
        </Grid.Row>
      </Grid>

      <Divider />

      {/* 模型使用分析 */}
      <Card fluid>
        <Card.Content>
          <Card.Header>
            <Icon name='cogs' />
            模型使用分析
          </Card.Header>
        </Card.Content>
        <Card.Content>
          {modelUsage?.distribution && modelUsage.distribution.length > 0 ? (
          <Table celled>
            <Table.Header>
              <Table.Row>
                  <Table.HeaderCell>模型名称</Table.HeaderCell>
                  <Table.HeaderCell>调用次数</Table.HeaderCell>
                  <Table.HeaderCell>占比</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
                {modelUsage.distribution.map((model, index) => (
                <Table.Row key={index}>
                  <Table.Cell>
                      <Label color='blue'>{model.model}</Label>
                  </Table.Cell>
                    <Table.Cell>{model.count.toLocaleString()}</Table.Cell>
                  <Table.Cell>
                      <Progress 
                        percent={model.percentage} 
                        size='small'
                        color='blue'
                        label={`${model.percentage?.toFixed(1)}%`}
                      />
                  </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
              <Table.Footer>
                <Table.Row>
                  <Table.HeaderCell>总计</Table.HeaderCell>
                  <Table.HeaderCell colSpan='2'>
                    <strong>{modelUsage.total?.toLocaleString()}</strong> 次调用
                  </Table.HeaderCell>
                </Table.Row>
              </Table.Footer>
            </Table>
          ) : (
            <Message>暂无模型使用数据</Message>
          )}
        </Card.Content>
      </Card>

      <Divider />

      {/* 渠道分析 */}
      <Card fluid>
        <Card.Content>
          <Card.Header>
            <Icon name='server' />
            渠道质量分析
          </Card.Header>
        </Card.Content>
        <Card.Content>
          {channelAnalysis && channelAnalysis.length > 0 ? (
            <Table celled>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>渠道</Table.HeaderCell>
                  <Table.HeaderCell>请求数</Table.HeaderCell>
                  <Table.HeaderCell>成功率</Table.HeaderCell>
                  <Table.HeaderCell>平均延迟</Table.HeaderCell>
                  <Table.HeaderCell>失败数</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {channelAnalysis.map((channel, index) => (
                  <Table.Row key={index}>
                  <Table.Cell>
                      <Label color='teal'>
                        {channel.channel_name || `渠道${channel.channel_id}`}
                    </Label>
                  </Table.Cell>
                    <Table.Cell>{channel.total_requests?.toLocaleString()}</Table.Cell>
                  <Table.Cell>
                      <Label 
                        color={channel.success_rate >= 99 ? 'green' : channel.success_rate >= 95 ? 'yellow' : 'red'}
                        size='small'
                      >
                        {channel.success_rate?.toFixed(1)}%
                      </Label>
                  </Table.Cell>
                  <Table.Cell>
                      <Label 
                        color={channel.avg_latency < 1000 ? 'green' : channel.avg_latency < 3000 ? 'yellow' : 'red'}
                        size='small'
                      >
                        {channel.avg_latency}ms
                      </Label>
                  </Table.Cell>
                  <Table.Cell>
                      {channel.failed_count > 0 ? (
                        <Label color='red' size='small'>{channel.failed_count}</Label>
                      ) : (
                        <Label color='green' size='small'>0</Label>
                      )}
                  </Table.Cell>
                </Table.Row>
                ))}
            </Table.Body>
          </Table>
          ) : (
            <Message>暂无渠道数据</Message>
          )}
        </Card.Content>
      </Card>
    </div>
  );
};

// API分析标签页
const APIAnalysisTab = ({ apiStats, timeRange, onTimeRangeChange }) => {
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82ca9d'];

  if (!apiStats) {
    return (
      <Segment placeholder>
        <Header icon>
          <Icon name='code' />
          暂无 API 统计数据
        </Header>
      </Segment>
    );
  }

  const { summary, endpoints } = apiStats;

  // 获取趋势指示器
  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up':
        return <Icon name='arrow up' color='green' />;
      case 'down':
        return <Icon name='arrow down' color='red' />;
      default:
        return <Icon name='minus' color='grey' />;
    }
  };

  return (
    <div>
      {/* API 概览卡片 */}
      <Grid stackable>
        <Grid.Row>
          <Grid.Column width={16}>
            <Card fluid color='blue'>
              <Card.Content>
                <Card.Header>
                  <Icon name='code' />
                  API 统计概览
                </Card.Header>
              </Card.Content>
              <Card.Content>
                <Statistic.Group size='small' widths='four'>
                  <Statistic color='blue'>
                    <Statistic.Value>{summary?.total_endpoints || 0}</Statistic.Value>
                    <Statistic.Label>活跃端点</Statistic.Label>
                  </Statistic>
                  <Statistic color='green'>
                    <Statistic.Value>{(summary?.total_requests || 0).toLocaleString()}</Statistic.Value>
                    <Statistic.Label>总请求数</Statistic.Label>
                  </Statistic>
                  <Statistic color='orange'>
                    <Statistic.Value>{(summary?.overall_success_rate || 0).toFixed(1)}%</Statistic.Value>
                    <Statistic.Label>整体成功率</Statistic.Label>
                  </Statistic>
                  <Statistic color='purple'>
                    <Statistic.Value>{summary?.avg_latency || 0}ms</Statistic.Value>
                    <Statistic.Label>平均延迟</Statistic.Label>
                  </Statistic>
                </Statistic.Group>
              </Card.Content>
            </Card>
          </Grid.Column>
        </Grid.Row>
      </Grid>

      <Divider />

      {/* API 端点排行 */}
      <Card fluid>
        <Card.Content>
          <Card.Header>
            <Icon name='list ol' />
            API 端点排行
            <Label size='small' color='blue' style={{ marginLeft: '10px' }}>
              按请求量排序
            </Label>
            <Label size='small' color='grey' style={{ marginLeft: '10px' }}>
              统计周期: 近 {timeRange || 7} 天
            </Label>
          </Card.Header>
        </Card.Content>
        <Card.Content style={{ overflowX: 'auto' }}>
          {endpoints && endpoints.length > 0 ? (
            <Table celled compact>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>端点</Table.HeaderCell>
                  <Table.HeaderCell>方法</Table.HeaderCell>
                  <Table.HeaderCell>请求数</Table.HeaderCell>
                  <Table.HeaderCell>成功率</Table.HeaderCell>
                  <Table.HeaderCell>错误数</Table.HeaderCell>
                  <Table.HeaderCell>平均延迟</Table.HeaderCell>
                  <Table.HeaderCell>最大延迟</Table.HeaderCell>
                  <Table.HeaderCell>用户数</Table.HeaderCell>
                  <Table.HeaderCell>趋势</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {endpoints.map((endpoint, index) => (
                  <Table.Row key={index}>
                    <Table.Cell>
                      <Popup
                        content={endpoint.endpoint}
                        trigger={
                          <span style={{ cursor: 'help' }}>
                            {endpoint.endpoint?.length > 35 ? endpoint.endpoint.substring(0, 35) + '...' : endpoint.endpoint}
                          </span>
                        }
                      />
                    </Table.Cell>
                    <Table.Cell>
                      <Label 
                        size='mini' 
                        color={endpoint.method === 'POST' ? 'green' : endpoint.method === 'GET' ? 'blue' : 'grey'}
                      >
                        {endpoint.method}
                      </Label>
                    </Table.Cell>
                    <Table.Cell>
                      <strong>{endpoint.total_requests?.toLocaleString()}</strong>
                    </Table.Cell>
                    <Table.Cell>
                      <Label 
                        color={endpoint.success_rate >= 99 ? 'green' : endpoint.success_rate >= 95 ? 'yellow' : 'red'}
                        size='small'
                      >
                        {endpoint.success_rate?.toFixed(1)}%
                      </Label>
                    </Table.Cell>
                    <Table.Cell>
                      {endpoint.error_count > 0 ? (
                        <Label color='red' size='small'>{endpoint.error_count}</Label>
                      ) : (
                        <Label color='green' size='small'>0</Label>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      <Label 
                        color={endpoint.avg_latency < 1000 ? 'green' : endpoint.avg_latency < 3000 ? 'yellow' : 'red'}
                        size='small'
                      >
                        {endpoint.avg_latency}ms
                      </Label>
                    </Table.Cell>
                    <Table.Cell>
                      {endpoint.max_latency}ms
                    </Table.Cell>
                    <Table.Cell>
                      {endpoint.unique_users}
                    </Table.Cell>
                    <Table.Cell>
                      {getTrendIcon(endpoint.trend)}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          ) : (
            <Message>暂无 API 端点数据</Message>
          )}
        </Card.Content>
      </Card>

      <Divider />

      {/* API 使用分布图表 */}
      {endpoints && endpoints.length > 0 && (
      <Grid stackable>
        <Grid.Row>
          <Grid.Column width={8}>
            <Card fluid>
                <Card.Content>
              <Card.Header>
                    <Icon name='pie chart' />
                    请求量分布 TOP10
              </Card.Header>
                </Card.Content>
              <Card.Content>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={endpoints.slice(0, 10).map(e => ({
                          name: e.endpoint?.split('/').pop() || e.endpoint,
                          value: e.total_requests
                        }))}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {endpoints.slice(0, 10).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => value.toLocaleString()} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
              </Card.Content>
            </Card>
          </Grid.Column>
          
          <Grid.Column width={8}>
            <Card fluid>
                <Card.Content>
              <Card.Header>
                    <Icon name='chart bar' />
                    延迟对比 TOP10 (毫秒)
              </Card.Header>
                </Card.Content>
              <Card.Content>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart 
                      data={endpoints.slice(0, 10).map(e => {
                        // 生成更友好的端点显示名称
                        const pathParts = (e.endpoint || '').split('/').filter(p => p);
                        let displayName = pathParts.length > 0 ? pathParts.slice(-2).join('/') : e.endpoint;
                        // 如果名称太长，截断并添加省略号
                        if (displayName.length > 25) {
                          displayName = '...' + displayName.slice(-22);
                        }
                        return {
                          name: displayName || 'Unknown',
                          avg: e.avg_latency || 0,
                          max: e.max_latency || 0
                        };
                      })}
                      layout="vertical"
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" unit="ms" />
                      <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 9 }} />
                      <Tooltip 
                        formatter={(value) => [`${value} ms`, '']}
                        labelFormatter={(label) => `端点: ${label}`}
                      />
                      <Legend />
                      <Bar dataKey="avg" name="平均延迟(ms)" fill="#8884d8" />
                      <Bar dataKey="max" name="最大延迟(ms)" fill="#82ca9d" />
                    </BarChart>
                  </ResponsiveContainer>
                </Card.Content>
              </Card>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      )}

      {/* API 质量说明 */}
      <Card fluid style={{ marginTop: '20px' }}>
        <Card.Content>
          <Card.Header>
            <Icon name='info circle' />
            指标说明
          </Card.Header>
        </Card.Content>
        <Card.Content>
          <Grid stackable columns={4}>
            <Grid.Column>
              <Header as='h5'>成功率</Header>
              <p style={{ fontSize: '12px', color: '#666' }}>
                HTTP 2xx 响应占总请求的比例。
                <br />
                <Label size='mini' color='green'>≥99%</Label> 优秀
                <Label size='mini' color='yellow'>95-99%</Label> 良好
                <Label size='mini' color='red'>&lt;95%</Label> 需关注
              </p>
            </Grid.Column>
            <Grid.Column>
              <Header as='h5'>平均延迟</Header>
              <p style={{ fontSize: '12px', color: '#666' }}>
                请求响应的平均时间。
                <br />
                <Label size='mini' color='green'>&lt;1s</Label> 优秀
                <Label size='mini' color='yellow'>1-3s</Label> 良好
                <Label size='mini' color='red'>&gt;3s</Label> 需优化
              </p>
            </Grid.Column>
            <Grid.Column>
              <Header as='h5'>趋势</Header>
              <p style={{ fontSize: '12px', color: '#666' }}>
                与前半周期相比的请求量变化。
                <br />
                <Icon name='arrow up' color='green' /> 上升
                <Icon name='arrow down' color='red' /> 下降
                <Icon name='minus' color='grey' /> 稳定
              </p>
            </Grid.Column>
            <Grid.Column>
              <Header as='h5'>独立用户</Header>
              <p style={{ fontSize: '12px', color: '#666' }}>
                使用该端点的不同用户数量，反映端点的受众广度。
              </p>
            </Grid.Column>
          </Grid>
        </Card.Content>
      </Card>
    </div>
  );
};

// 存储分析标签页
const StorageAnalysisTab = ({ overview, tableStats }) => {
  // 格式化文件大小
  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (!overview) {
    return (
      <Segment placeholder>
        <Header icon>
          <Icon name='database' />
          暂无存储统计数据
        </Header>
      </Segment>
    );
  }

  const { mysql, redis } = overview;

  return (
    <div>
      {/* MySQL 和 Redis 状态卡片 */}
      <Grid stackable>
        <Grid.Row columns={2}>
          {/* MySQL 状态 */}
          <Grid.Column>
            <Card fluid color='blue'>
              <Card.Content>
                <Card.Header>
                  <Icon name='server' />
                  MySQL 数据库
                </Card.Header>
              </Card.Content>
              <Card.Content>
                <Statistic.Group size='small' widths='three'>
                  <Statistic>
                    <Statistic.Value>{formatBytes(mysql?.database_size)}</Statistic.Value>
                    <Statistic.Label>数据库大小</Statistic.Label>
                  </Statistic>
                  <Statistic>
                    <Statistic.Value>{mysql?.table_count || 0}</Statistic.Value>
                    <Statistic.Label>表数量</Statistic.Label>
                  </Statistic>
                  <Statistic>
                    <Statistic.Value>{(mysql?.total_rows || 0).toLocaleString()}</Statistic.Value>
                    <Statistic.Label>总行数</Statistic.Label>
                  </Statistic>
                </Statistic.Group>
                <Divider />
                <Statistic.Group size='mini' widths='three'>
                  <Statistic>
                    <Statistic.Value>{mysql?.qps?.toFixed(2) || 0}</Statistic.Value>
                    <Statistic.Label>QPS</Statistic.Label>
                  </Statistic>
                  <Statistic>
                    <Statistic.Value>
                      {mysql?.buffer_pool_hit_rate?.toFixed(1) || 0}%
                    </Statistic.Value>
                    <Statistic.Label>缓冲池命中率</Statistic.Label>
                  </Statistic>
                  <Statistic>
                    <Statistic.Value>
                      {mysql?.connection_usage?.toFixed(1) || 0}%
                    </Statistic.Value>
                    <Statistic.Label>连接使用率</Statistic.Label>
                  </Statistic>
                </Statistic.Group>
                <Divider />
                <div style={{ fontSize: '12px', color: '#666' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                    <span>当前连接:</span>
                    <span>{mysql?.current_connections || 0} / {mysql?.max_connections || 0}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                    <span>活跃连接:</span>
                    <span>{mysql?.active_connections || 0}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>慢查询:</span>
                    <span style={{ color: (mysql?.slow_queries || 0) > 0 ? 'red' : 'inherit' }}>
                      {mysql?.slow_queries || 0}
                    </span>
                  </div>
                </div>
              </Card.Content>
            </Card>
          </Grid.Column>

          {/* Redis 状态 */}
          <Grid.Column>
            <Card fluid color={redis ? 'red' : 'grey'}>
              <Card.Content>
                <Card.Header>
                  <Icon name='lightning' />
                  Redis 缓存
                  {!redis && <Label size='tiny' color='grey' style={{ marginLeft: '10px' }}>未启用</Label>}
                </Card.Header>
              </Card.Content>
              <Card.Content>
                {redis ? (
                  <>
                    <Statistic.Group size='small' widths='three'>
                      <Statistic>
                        <Statistic.Value>{redis.used_memory_human}</Statistic.Value>
                        <Statistic.Label>内存使用</Statistic.Label>
                      </Statistic>
                      <Statistic>
                        <Statistic.Value>{redis.total_keys?.toLocaleString() || 0}</Statistic.Value>
                        <Statistic.Label>Key 数量</Statistic.Label>
                      </Statistic>
                      <Statistic>
                        <Statistic.Value>{redis.hit_rate?.toFixed(1) || 0}%</Statistic.Value>
                        <Statistic.Label>命中率</Statistic.Label>
                      </Statistic>
                    </Statistic.Group>
                    <Divider />
                    <Statistic.Group size='mini' widths='three'>
                      <Statistic>
                        <Statistic.Value>{redis.ops_per_sec || 0}</Statistic.Value>
                        <Statistic.Label>QPS</Statistic.Label>
                      </Statistic>
                      <Statistic>
                        <Statistic.Value>{redis.mem_fragmentation?.toFixed(2) || 0}</Statistic.Value>
                        <Statistic.Label>碎片率</Statistic.Label>
                      </Statistic>
                      <Statistic>
                        <Statistic.Value>{redis.connected_clients || 0}</Statistic.Value>
                        <Statistic.Label>连接数</Statistic.Label>
                      </Statistic>
                    </Statistic.Group>
                    <Divider />
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                        <span>版本:</span>
                        <span>{redis.redis_version}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                        <span>运行时间:</span>
                        <span>{Math.floor((redis.uptime_seconds || 0) / 86400)} 天</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>驱逐 Key:</span>
                        <span style={{ color: (redis.evicted_keys || 0) > 0 ? 'red' : 'inherit' }}>
                          {redis.evicted_keys?.toLocaleString() || 0}
                        </span>
                      </div>
                    </div>
                  </>
                ) : (
                  <Message info>
                    <Message.Header>Redis 未启用</Message.Header>
                    <p>系统未配置 Redis 缓存服务。</p>
                  </Message>
                )}
              </Card.Content>
            </Card>
          </Grid.Column>
        </Grid.Row>
      </Grid>

      <Divider />

      {/* MySQL 表统计 */}
      <Card fluid>
        <Card.Content>
        <Card.Header>
            <Icon name='table' />
            MySQL 表统计
        </Card.Header>
        </Card.Content>
        <Card.Content style={{ overflowX: 'auto' }}>
          {tableStats && tableStats.length > 0 ? (
            <Table celled compact sortable>
            <Table.Header>
              <Table.Row>
                  <Table.HeaderCell>表名</Table.HeaderCell>
                  <Table.HeaderCell>引擎</Table.HeaderCell>
                  <Table.HeaderCell>行数</Table.HeaderCell>
                  <Table.HeaderCell>数据大小</Table.HeaderCell>
                  <Table.HeaderCell>索引大小</Table.HeaderCell>
                  <Table.HeaderCell>总大小</Table.HeaderCell>
                  <Table.HeaderCell>占比</Table.HeaderCell>
                  <Table.HeaderCell>碎片率</Table.HeaderCell>
                  <Table.HeaderCell>更新时间</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
                {tableStats.map((table, index) => {
                  const fragmentColor = 
                    table.fragmentation_rate > 30 ? 'red' : 
                    table.fragmentation_rate > 10 ? 'orange' : 'green';
                
                return (
                  <Table.Row key={index}>
                      <Table.Cell><strong>{table.table_name}</strong></Table.Cell>
                      <Table.Cell>{table.engine}</Table.Cell>
                      <Table.Cell>{table.row_count?.toLocaleString()}</Table.Cell>
                      <Table.Cell>{formatBytes(table.data_size)}</Table.Cell>
                      <Table.Cell>{formatBytes(table.index_size)}</Table.Cell>
                      <Table.Cell><strong>{formatBytes(table.total_size)}</strong></Table.Cell>
                    <Table.Cell>
                        <Progress 
                          percent={table.percentage?.toFixed(1)} 
                          size='tiny' 
                          color='blue'
                          progress
                        />
                    </Table.Cell>
                    <Table.Cell>
                        <Label size='small' color={fragmentColor}>
                          {table.fragmentation_rate?.toFixed(2)}%
                      </Label>
                    </Table.Cell>
                    <Table.Cell>
                        {table.updated_at ? new Date(table.updated_at).toLocaleString() : '-'}
                    </Table.Cell>
                  </Table.Row>
                );
                })}
            </Table.Body>
          </Table>
          ) : (
            <Message>暂无表统计数据</Message>
          )}
        </Card.Content>
      </Card>

      {/* 优化建议 */}
      {mysql && (
        <Card fluid>
          <Card.Content>
            <Card.Header>
              <Icon name='lightbulb' />
              优化建议
            </Card.Header>
          </Card.Content>
          <Card.Content>
            <Message.List>
              {mysql.buffer_pool_hit_rate < 95 && (
                <Message warning>
                  <Icon name='warning sign' />
                  缓冲池命中率 ({mysql.buffer_pool_hit_rate?.toFixed(1)}%) 低于推荐值 95%，建议增加 innodb_buffer_pool_size
                </Message>
              )}
              {mysql.connection_usage > 80 && (
                <Message warning>
                  <Icon name='warning sign' />
                  连接使用率 ({mysql.connection_usage?.toFixed(1)}%) 较高，建议增加 max_connections 或优化连接池
                </Message>
              )}
              {mysql.slow_queries > 100 && (
                <Message warning>
                  <Icon name='warning sign' />
                  检测到 {mysql.slow_queries} 个慢查询，建议检查慢查询日志并优化
                </Message>
              )}
              {tableStats && tableStats.filter(t => t.fragmentation_rate > 30).length > 0 && (
                <Message warning>
                  <Icon name='warning sign' />
                  {tableStats.filter(t => t.fragmentation_rate > 30).length} 个表碎片率超过 30%，建议执行 OPTIMIZE TABLE
                </Message>
              )}
              {redis && redis.mem_fragmentation > 1.5 && (
                <Message warning>
                  <Icon name='warning sign' />
                  Redis 内存碎片率 ({redis.mem_fragmentation?.toFixed(2)}) 较高，建议执行 MEMORY PURGE 或重启
                </Message>
              )}
              {redis && redis.evicted_keys > 0 && (
                <Message warning>
                  <Icon name='warning sign' />
                  Redis 已驱逐 {redis.evicted_keys?.toLocaleString()} 个 Key，建议增加内存或调整淘汰策略
                </Message>
              )}
              {!mysql.buffer_pool_hit_rate || mysql.buffer_pool_hit_rate >= 95 && 
               mysql.connection_usage < 80 && 
               mysql.slow_queries < 100 &&
               (!redis || (redis.mem_fragmentation <= 1.5 && redis.evicted_keys === 0)) && (
                <Message success>
                  <Icon name='check circle' />
                  系统存储状态良好，无需优化
                </Message>
              )}
            </Message.List>
          </Card.Content>
        </Card>
      )}
    </div>
  );
};

export default DataAnalytics;
